import { OKTAUserTypeEnum } from "@app/hooks/useUserInfo";
import AdsContainer from "@components/Blocks/Container/AdsContainer";
import {
  CreateLoadMoreHandler,
  CreateLoadMoreHandlerProps,
} from "@components/Modules/LoadMore/CreateLoadMoreHandler";
import LoadMore from "@components/Modules/LoadMore/LoadMore";
import { LOAD_MORE_CTA_COPY } from "@config/ctaCopy";
import Taboola from "@elements/Advertisement/Taboola";
import { CompanionAdsLb1 } from "@elements/Advertisement/variant/CompanionAdsLb";
import { getTagsName } from "@elements/GAData/GADataHelper";
import { LabelCardIconVariety } from "@elements/Icon/LabelCardIcon";
import fetchBHApi from "@helper/fetchBHApi";
import { BH_BASE_URL } from "@helper/getEnvVariables";
import { BEYOND_WORDS_PROJECT_ID } from "@helper/getEnvVariables";
import { removeTrailingSlash } from "@helper/removeTrailingLeadingSlash";
import { shouldShowTTSPlayer } from "@helper/showTTS";
import { isArrayWithElements, isClientSide } from "@helper/utils";
import { LatestVideoResponse } from "@hooks/transformer/useBrightcoveResponse";
import { useAudioAnalytics } from "@hooks/useAnalyticsCall/useAudioAnalytics";
import useLongFormVideo from "@hooks/useBrightcoveVideo/useLongFormVideo";
import useOmnyPodcast from "@hooks/useOmnyPodcast";
import useVisibleArticle from "@hooks/useVisibleArticle";
import { scrollFlags } from "@src/app/components/Elements/BeyondWordsPlayer/events";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { AdTargetingType, ArticleAdTargetKeys } from "@typings/Ads.d";
import {
  FetchMoreArticlesFunction,
  MoreArticlesRequestData,
} from "@typings/MoreArticlesApi";
import Cookies from "js-cookie";
import React, { RefObject, useEffect, useState } from "react";

import ArticleContentCardNew from "./ArticleLayout/ArticleContentCardNew";
import ArticleElementWrapper from "./Components/ArticleElementWrapper";
import LongFormVideo from "./Components/LongFormVideo";
import MediaPodcast from "./Components/MediaPodcast";
import ShortFormVideos from "./Components/ShortFormVideos";

type ArticleInfiniteScrollProps = {
  mainArticle: ProcessedArticleData;
  onNewArticle: (
    article: ProcessedArticleData,
    pageNumber: number,
    viewPortChange: boolean,
  ) => void;
  isTaboolaEnabled: boolean;
  PubPageAdTarget: AdTargetingType<ArticleAdTargetKeys, string>;
  containerRef: RefObject<HTMLDivElement>;
  videos: LatestVideoResponse[];
  handleOnEndedVideo: () => void;
  handleOnClickThumbnail: (props: LatestVideoResponse) => void;
  playing?: LatestVideoResponse;
  containerRefLong: RefObject<HTMLDivElement>;
};

export type ArticleVariantData = {
  payload: {
    article: {
      variant: string;
    };
  };
};

export default function ArticleInfiniteScroll({
  mainArticle,
  onNewArticle,
  isTaboolaEnabled = false,
  PubPageAdTarget,
  containerRef,
  videos,
  handleOnEndedVideo,
  handleOnClickThumbnail,
  playing,
  containerRefLong,
}: ArticleInfiniteScrollProps) {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>([mainArticle.id]),
  );
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [visibleArticleId, setVisibleArticleId] = useState<string>(
    mainArticle.id,
  );

  // Trigger useVisibleArticle when LoadMoreData changes
  const visibleArticleIdFromHook = useVisibleArticle(visibleArticleId);
  const [currentPath, setCurrentPath] = useState<string>(
    mainArticle.urlPath || "",
  );
  const { playing: longVideo } = useLongFormVideo();
  const { playing: podcast } = useOmnyPodcast();
  const audioTrackingEvent = useAudioAnalytics();

  const filterArticlesBasedOn = mainArticle.parentSection
    ? mainArticle.linkToSection.split("/").at(-1)
    : mainArticle.uniqueSectionName;

  const fetchNextStoryData = async (
    requestData: MoreArticlesRequestData,
  ): Promise<ProcessedArticleData[]> => {
    const queryParams = new URLSearchParams({
      section: requestData.filterArticlesBasedOn as string,
      excludeIds: requestData.excludeIds as string,
    }).toString();
    const response: ProcessedArticleData[] = await fetchBHApi(
      `get-article?${queryParams}`,
      "GET",
    );
    console.log("debug fetched get-article data");
    const userType = Cookies.get("mySPHUserType");
    if (
      response.length > 0 &&
      response[0].urlPath &&
      response[0].contentAccess === LabelCardIconVariety.FREE &&
      userType === OKTAUserTypeEnum.ANONYMOUS
    ) {
      console.log("debug variant call condition is success");
      try {
        const contextJsonUrl = `${removeTrailingSlash(BH_BASE_URL)}${response[0].urlPath}/context.json`;
        const contextResponse = await fetch(contextJsonUrl, {
          method: "GET",
        });
        if (!contextResponse.ok) {
          throw new Error(
            `Failed to fetch context JSON: ${contextResponse.statusText}`,
          );
        }
        let additionalResponse;
        try {
          additionalResponse =
            (await contextResponse.json()) as ArticleVariantData;
        } catch (error) {
          throw new Error("Failed to parse context JSON.");
        }
        response[0].variant =
          additionalResponse.payload.article.variant || "default custom";
        console.log("debug variant from api", response[0].variant);
      } catch (error) {
        response[0].variant = "default error";
        console.error("Error loading or processing context.json:", error);
      }
      // response[0].title = `${response[0].variant}: ${response[0].title}`;
    }

    return response;
  };
  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length === 0 ? mainArticle : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn,
      excludeIds: [...renderedArticleIds].join(","),
    };

    const fetchFunction: FetchMoreArticlesFunction = fetchNextStoryData;
    const updateGADataForArticle = true;

    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: 1,
      pageNumber,
      updateGADataForArticle,
    };

    const response = await CreateLoadMoreHandler(createLoadMoreProps);

    if (isArrayWithElements(response)) {
      const article = response[0];
      // Update the URL and visibleArticleId when the article is loaded
      setCurrentPath(article.urlPath || "");
      setTimeout(() => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const player = window.beyondWordsPlayerInstances?.[article.id];
          if (
            player &&
            shouldShowTTSPlayer(article.datetime, article.contentAccess)
          ) {
            // Keep track of whether the player was playing when the identifiers change.
            // Initialize previousState from the current player state to avoid using it before assignment.
            let previousState = "paused";
            player.addEventListener("IdentifiersChanged", () => {
              previousState = player.playbackState;
            });
            if (previousState === "playing" || scrollFlags.isProgrammatic) {
              const audioId = `${BEYOND_WORDS_PROJECT_ID}::${article.id}`;
              const audioDuration = Number(player.duration) || 0;
              const audioListenedTime = Number(player.currentTime) || 0;
              const audioSpeed = Number(player.playbackRate) || 1;
              const audioListenedpercentage =
                audioDuration === 0
                  ? 0
                  : Math.floor((audioListenedTime / audioDuration) * 100);

              audioTrackingEvent(
                "audio_start",
                article.title,
                audioId,
                audioListenedTime,
                audioDuration,
                audioListenedpercentage,
                audioSpeed,
                "yes",
              );
              player.playbackState = "playing";
              scrollFlags.isProgrammatic = false; // Reset the flag after playback starts
            }
          }
        } catch (error) {
          console.error(
            "Error starting beyondWords player after timeout:",
            error,
          );
        }
      }, 3000);
      setVisibleArticleId(article.id);
      onNewArticle(article, pageNumber + 1, false);
    } else {
      setHasMoreStories(false);
    }

    return response;
  };

  useEffect(() => {
    if (!isClientSide || !visibleArticleId) return;

    const allArticles = [mainArticle, ...loadMoreData];
    const visibleArticleIndex = allArticles.findIndex(
      (article) => article.id === visibleArticleId,
    );

    const articleData =
      visibleArticleIndex !== -1 ? allArticles[visibleArticleIndex] : undefined;

    if (articleData) {
      const newPath = articleData.urlPath;
      if (newPath && newPath !== currentPath) {
        window.history.pushState("article", articleData.title, newPath);
        setCurrentPath(newPath);
        if (
          typeof window.beyondWordsPlayerInstances?.[articleData.id] !==
            "undefined" &&
          shouldShowTTSPlayer(articleData.datetime, articleData.contentAccess)
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          window.beyondWordsPlayerInstances[articleData.id].sourceId =
            articleData.id;
        }
        onNewArticle(articleData, visibleArticleIndex, true); // Notify parent component
      }
    }
  }, [visibleArticleId, loadMoreData, mainArticle, onNewArticle, currentPath]);

  useEffect(() => {
    if (!isClientSide) return;
    if (visibleArticleIdFromHook) {
      setVisibleArticleId(visibleArticleIdFromHook);
    }
  }, [visibleArticleIdFromHook]);

  return (
    <div>
      {[...loadMoreData].map((context, index) => {
        const mod = index % 3;
        return (
          <React.Fragment key={context.id}>
            <AdsContainer className="my-6 lg:my-8">
              <CompanionAdsLb1
                uniqueSectionName={context.adsUniqueSectionName}
                stripeBackground={false}
                index={index + 1}
                gsChannels={context.gsChannels}
                articleSlotTarget={[
                  { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                  {
                    key: ArticleAdTargetKeys.TAGS,
                    value: getTagsName(context.tags || []).toLowerCase(),
                  },
                  PubPageAdTarget,
                ]}
              />
            </AdsContainer>
            <div id={context.id} className="article-card">
              <>
                <ArticleContentCardNew
                  mainArticle={context}
                  pageNumber={index + 1}
                  isInfiniteScrollArticle={true}
                  gsChannels={context.gsChannels}
                  articleSlotTarget={[
                    { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                    {
                      key: ArticleAdTargetKeys.TAGS,
                      value: getTagsName(context.tags || []).toLowerCase(),
                    },
                    PubPageAdTarget,
                  ]}
                  index={index}
                  visibleArticleId={visibleArticleId}
                />
                <ArticleElementWrapper>
                  {mod === 0 && longVideo && (
                    <LongFormVideo
                      containerRef={containerRefLong}
                      playing={longVideo}
                    />
                  )}
                  {mod === 1 && podcast && (
                    <MediaPodcast
                      containerRef={containerRefLong}
                      playing={podcast}
                    />
                  )}
                  {mod === 2 && (
                    <ShortFormVideos
                      containerRef={containerRef}
                      videos={videos}
                      handleOnClickThumbnail={handleOnClickThumbnail}
                      handleOnEndedVideo={handleOnEndedVideo}
                      playing={playing}
                    />
                  )}
                  {isTaboolaEnabled && context.urlPath && (
                    <div className="mt-4 lg:mt-6">
                      <Taboola
                        counter={index + 1}
                        pathName={context.urlPath}
                        pageType="article"
                        mode="thumbnails-base-widget"
                        container="below-article-thumbnails"
                        placement="Below Article Thumbnails"
                        targetType="mix"
                      />
                    </div>
                  )}
                </ArticleElementWrapper>
              </>
            </div>
          </React.Fragment>
        );
      })}
      <ArticleElementWrapper className="mt-12 mb-6 lg:mt-20 lg:mb-10">
        <LoadMore
          rootClassName="my-4"
          onLoadMore={handleLoadMore}
          hasMore={hasMoreStories}
          loadText={LOAD_MORE_CTA_COPY}
          isArticlePage={true}
          virtualPVTitle={mainArticle.title}
          pageNumber={pageNumber}
          autoLoadOnMobile={true}
        />
      </ArticleElementWrapper>
    </div>
  );
}
